import * as _ from 'lodash';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isProjects, isReleaseGroups, isSharedProjects } from '../../../../../shared/projectsV2';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import FallbackImage from '../../../generic/FallbackImage';
import AddProjectsCTA from '../../shared/CTAs/AddProjectsCTA';
import AddReleaseGroupCTA from '../../shared/CTAs/AddReleaseGroupCTA';

import LoadingProjectsTableBody from './LoadingProjectsTableBody';
import ProjectsBulkSelectionBanner from './ProjectBulkSelectionBanner';
import ProjectsTableBody from './ProjectsTableBody';
import ProjectsTableHeader from './ProjectsTableHeader';

const ProjectsTable: VoidFunctionComponent = () => {
  const { loading: isLoading, errors } = useSelector(ProjectsV2Store.selectors.getAsyncStatusFetchProjects);
  const category = useSelector(ProjectsV2Store.selectors.getCategory);
  const projects = useSelector(ProjectsV2Store.selectors.getProjects);
  const pageSize = useSelector(ProjectsV2Store.selectors.getCount);
  const hasFilterApplied = useSelector(ProjectsV2Store.selectors.getHasFilterApplied);

  const renderProjectsTableBody = () => {
    // show loading rows animation if waiting for backend query
    if (isLoading) {
      return <LoadingProjectsTableBody count={pageSize} />;
    }

    // show error state if errors exist
    if (!_.isEmpty(errors)) {
      return (
        <FallbackImage
          src="error-plug"
          alt="Network error"
          hasPadding
        >
          <div>Oops! There was an error.</div>
          <div>Please try again in a bit.</div>
        </FallbackImage>
      );
    }

    // display a fallback if the given search / filters return no projects OR an org has no projects yet
    if (_.isEmpty(projects)) {
      // if the user has entered a title search / filter, show them a "no results found" style message
      if (hasFilterApplied || isSharedProjects(category)) {
        return (
          <FallbackImage
            src="no-results"
            alt="No projects found"
            hasPadding
          >
            We couldn&apos;t find any results.
          </FallbackImage>
        );
      }

      // show CTA for adding a new project
      if (isProjects(category)) {
        return <AddProjectsCTA />;
      }

      // show CTA for adding a new release group
      if (isReleaseGroups(category)) {
        return <AddReleaseGroupCTA />;
      }
    }

    return <ProjectsTableBody category={category} projects={projects} />;
  };

  return (
    <div className="projects-table">
      <ProjectsTableHeader />
      <ProjectsBulkSelectionBanner />
      {renderProjectsTableBody()}
    </div>
  );
};

export default ProjectsTable;
